import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  untracked,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import * as L from 'leaflet';
import type { DivIcon, LayerGroup, Map as LeafletMap } from 'leaflet';
import { MapMarker } from '../../model/map-marker';
import { UserCoordinates, UserGeolocationService } from '../../services/user-geolocation.service';
import { distanceFromUserLabel, distanceKm } from '../../utils/geo-distance';

const ITALY_CENTER: [number, number] = [42.5, 12.5];
const ITALY_ZOOM = 6;
const USER_NEARBY_KM = 150;
const MAX_ACCURACY_CIRCLE_M = 5000;

const MARKER_ICONS: Record<MapMarker['kind'], string> = {
  event: 'bi-calendar-event',
  organization: 'bi-building',
  table: 'bi-dice-5',
};

@Component({
  selector: 'app-osm-map',
  standalone: true,
  templateUrl: './osm-map.component.html',
  styleUrl: './osm-map.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class OsmMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('mapEl', { static: true }) private mapEl!: ElementRef<HTMLDivElement>;

  @Input() markers: MapMarker[] = [];
  @Input() compact = false;
  @Input() ariaLabel = 'Mappa OpenStreetMap';
  @Input() emptyMessage = 'Nessun punto con coordinate da mostrare.';

  protected readonly userGeo = inject(UserGeolocationService);

  private map?: LeafletMap;
  private markerLayer?: LayerGroup;
  private userLayer?: LayerGroup;
  private resizeObserver?: ResizeObserver;
  private sizeTimers: number[] = [];
  private pendingCenterOnUser = false;
  private didFitWithUser = false;
  private userMovedMap = false;
  private programmaticViewChange = false;
  private markersFingerprint = '';
  private destroyed = false;

  constructor() {
    effect(() => {
      const position = this.userGeo.position();
      untracked(() => this.syncUserLocation(position));
    });
  }

  ngAfterViewInit(): void {
    this.userGeo.ensureLocated();
    if (this.destroyed) {
      return;
    }
    this.initMap();
    this.syncMarkers();
    this.syncUserLocation(this.userGeo.position());
    this.observeResize();
    this.scheduleInvalidateSize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['markers'] && !changes['markers'].firstChange && this.map) {
      this.syncMarkers();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    this.sizeTimers.forEach((timer) => window.clearTimeout(timer));
    this.sizeTimers = [];
    try {
      this.map?.stop();
      this.map?.remove();
    } catch {
      // Leaflet can throw if a zoom frame runs after the pane is gone.
    }
    this.map = undefined;
  }

  protected onLocate(): void {
    this.pendingCenterOnUser = true;
    this.userMovedMap = false;
    const position = this.userGeo.position();
    if (position) {
      this.centerOnUser(position);
      this.pendingCenterOnUser = false;
    }
    this.userGeo.requestPosition(true);
  }

  protected locateHint(): string {
    const status = this.userGeo.status();
    if (status === 'denied' || status === 'unavailable') {
      return 'Posizione non disponibile. Tocca il pulsante per riprovare.';
    }
    return '';
  }

  private initMap(): void {
    if (this.map) {
      return;
    }

    this.map = L.map(this.mapEl.nativeElement, {
      scrollWheelZoom: true,
      attributionControl: true,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
    }).setView(ITALY_CENTER, ITALY_ZOOM);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>',
    }).addTo(this.map);

    this.markerLayer = L.layerGroup().addTo(this.map);
    this.userLayer = L.layerGroup().addTo(this.map);

    this.map.on('dragstart', () => {
      this.userMovedMap = true;
    });
    this.map.on('zoomstart', () => {
      if (!this.programmaticViewChange) {
        this.userMovedMap = true;
      }
    });

    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'leaflet/images/marker-icon-2x.png',
      iconUrl: 'leaflet/images/marker-icon.png',
      shadowUrl: 'leaflet/images/marker-shadow.png',
    });
  }

  private syncMarkers(): void {
    if (!this.map || !this.markerLayer) {
      return;
    }

    const points = this.mappableMarkers();
    const fingerprint = this.fingerprintMarkers(points);
    if (fingerprint === this.markersFingerprint) {
      return;
    }
    this.markersFingerprint = fingerprint;

    this.markerLayer.clearLayers();
    for (const marker of points) {
      L.marker([marker.lat, marker.lng], { icon: this.createIcon(marker), title: marker.title })
        .bindPopup(() => this.popupHtml(marker))
        .addTo(this.markerLayer);
    }

    if (!this.userMovedMap) {
      this.fitToMarkers(points);
    }
    this.scheduleInvalidateSize();
  }

  private syncUserLocation(position: UserCoordinates | null): void {
    if (!this.map || !this.userLayer) {
      return;
    }

    this.userLayer.clearLayers();
    if (position) {
      if (
        position.accuracy &&
        position.accuracy > 0 &&
        position.accuracy <= MAX_ACCURACY_CIRCLE_M
      ) {
        L.circle([position.lat, position.lng], {
          radius: position.accuracy,
          color: '#1d4ed8',
          weight: 1,
          fillColor: '#3b82f6',
          fillOpacity: 0.15,
          interactive: false,
        }).addTo(this.userLayer);
      }

      L.marker([position.lat, position.lng], {
        icon: this.createUserIcon(),
        title: 'La tua posizione',
        zIndexOffset: 1000,
      })
        .bindPopup('Sei qui')
        .addTo(this.userLayer);
    }

    if (this.pendingCenterOnUser && position) {
      this.centerOnUser(position);
      this.pendingCenterOnUser = false;
      return;
    }

    if (!this.compact && !this.didFitWithUser && !this.userMovedMap && position) {
      this.fitToMarkers(this.mappableMarkers());
      this.didFitWithUser = true;
    }
  }

  private fitToMarkers(points: MapMarker[]): void {
    if (!this.map || this.userMovedMap) {
      return;
    }

    const user = this.compact ? null : this.userGeo.position();
    const includeUser = !!user && this.isUserNearMarkers(points, user);
    const latLngs: [number, number][] = points.map((marker) => [marker.lat, marker.lng]);
    if (includeUser && user) {
      latLngs.push([user.lat, user.lng]);
    }

    this.withProgrammaticView(() => {
      if (latLngs.length === 0) {
        if (user && !this.compact) {
          this.map?.setView([user.lat, user.lng], 13);
          return;
        }
        this.map?.setView(ITALY_CENTER, ITALY_ZOOM);
        return;
      }
      if (latLngs.length === 1) {
        this.map?.setView(latLngs[0], 13);
        return;
      }

      const bounds = L.latLngBounds(latLngs);
      this.map?.fitBounds(bounds, { padding: [36, 36], maxZoom: 14 });
    });
  }

  private isUserNearMarkers(points: MapMarker[], user: UserCoordinates): boolean {
    if (points.length === 0) {
      return true;
    }
    return points.some(
      (marker) => distanceKm(user, { lat: marker.lat, lng: marker.lng }) <= USER_NEARBY_KM,
    );
  }

  private mappableMarkers(): MapMarker[] {
    return this.markers.filter(
      (marker) => Number.isFinite(marker.lat) && Number.isFinite(marker.lng),
    );
  }

  private centerOnUser(position: UserCoordinates): void {
    if (!this.map) {
      return;
    }
    this.withProgrammaticView(() => {
      this.map?.setView([position.lat, position.lng], Math.max(this.map?.getZoom() ?? 14, 14));
    });
  }

  private withProgrammaticView(action: () => void): void {
    this.programmaticViewChange = true;
    try {
      action();
    } finally {
      this.programmaticViewChange = false;
    }
  }

  private fingerprintMarkers(points: MapMarker[]): string {
    return points.map((marker) => `${marker.id}:${marker.lat}:${marker.lng}`).join('|');
  }

  private createIcon(marker: MapMarker): DivIcon {
    const icon = MARKER_ICONS[marker.kind] ?? MARKER_ICONS.event;
    return L.divIcon({
      className: `osm-marker osm-marker--${marker.kind}`,
      html: `<span class="osm-marker__pin" aria-hidden="true"><i class="bi ${icon}"></i></span>`,
      iconSize: [36, 36],
      iconAnchor: [18, 36],
      popupAnchor: [0, -32],
    });
  }

  private createUserIcon(): DivIcon {
    return L.divIcon({
      className: 'osm-marker osm-marker--user',
      html: '<span class="osm-user-dot" data-testid="osm-user-marker" aria-hidden="true"></span>',
      iconSize: [18, 18],
      iconAnchor: [9, 9],
      popupAnchor: [0, -10],
    });
  }

  private popupHtml(marker: MapMarker): string {
    const subtitle = marker.subtitle
      ? `<div class="osm-popup__subtitle">${escapeHtml(marker.subtitle)}</div>`
      : '';
    const distance = distanceFromUserLabel(this.userGeo.position(), {
      lat: marker.lat,
      lng: marker.lng,
    });
    const distanceHtml = distance
      ? `<div class="osm-popup__distance">${escapeHtml(`${distance} da te`)}</div>`
      : '';
    const link = marker.linkHref
      ? `<a class="osm-popup__link" href="${escapeHtml(marker.linkHref)}">${escapeHtml(
          marker.linkLabel || 'Apri dettaglio',
        )}</a>`
      : '';
    const extraLinks = (marker.extraLinks ?? [])
      .map(
        (item) =>
          `<a class="osm-popup__link" href="${escapeHtml(item.href)}">${escapeHtml(item.label)}</a>`,
      )
      .join('');

    return `<div class="osm-popup">
      <div class="osm-popup__title">${escapeHtml(marker.title)}</div>
      ${subtitle}
      ${distanceHtml}
      ${link}
      ${extraLinks}
    </div>`;
  }

  private observeResize(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.map?.invalidateSize());
    this.resizeObserver.observe(this.mapEl.nativeElement);
  }

  private scheduleInvalidateSize(): void {
    [0, 200, 500].forEach((delay) => {
      const timer = window.setTimeout(() => this.map?.invalidateSize(), delay);
      this.sizeTimers.push(timer);
    });
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
